import React, { useEffect, useState } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { parseISO, format } from 'date-fns';
import { fetchActiveUsersByWeek } from '../services/api'; 
import Header from '../components/Header';

/**
 * Transforms the raw weekly data from the API into a format
 * that can be used by the Nivo line chart.
 * @param {Array} data - The raw data from the fetchActiveUsersByWeek API call.
 * @returns {Array} - The transformed data structured for Nivo.
 */
const transformWeeklyData = (data) => {
  const sortedData = [...data].sort((a, b) => new Date(a.day) - new Date(b.day));

  return [
    {
      id: 'Total # of Unique Users Logged in a Week',
      color: 'hsl(217, 89%, 61%)', 
      data: sortedData.map(({ day, count }) => ({
        // Format the date for the x-axis, e.g., "Feb 17, 2025"
        x: format(parseISO(day), 'MMM d, yyyy'),
        y: count,
      })),
    },
    {
      id: 'Total # of Unique Educators Logged in a Week',
      color: 'hsl(187, 100%, 42%)', 
      data: sortedData.map(({ day, eds }) => ({
        x: format(parseISO(day), 'MMM d, yyyy'),
        y: eds,
      })),
    },
    {
      id: 'Total # of Unique YWs Logged in a Week',
      color: 'hsl(333, 80%, 50%)', 
      data: sortedData.map(({ day, yws }) => ({
        x: format(parseISO(day), 'MMM d, yyyy'),
        y: yws,
      })),
    },
  ];
};

/**
 * A component to display weekly unique user login trends in a line chart.
 */
export default function WeeklyUniqueUserTrends() {
  const [weeklyData, setWeeklyData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getChartData = async () => {
      try { 
        const apiData = await fetchActiveUsersByWeek();
        if (apiData) {
          setWeeklyData(transformWeeklyData(apiData));
        }
      } catch (err) {
        setError('Failed to fetch user data.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    getChartData();
  }, []); 

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading Weekly Unique User Trends...</div>
      </div>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div>
    <Header pageTitle="WtW Weekly Unique User Trends" />
    <div className="p-4 bg-gray-50 min-h-screen">
      <div style={{ height: '600px' }} className="bg-white rounded-lg shadow-md p-4">
        <ResponsiveLine
          data={weeklyData}
          margin={{ top: 50, right: 50, bottom: 100, left: 80 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 'auto',
            max: 'auto',
            stacked: false,
            reverse: false,
          }}
          yFormat=" >-.2f"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            orient: 'bottom',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: -45,
            legend: 'Week',
            legendOffset: 80,
            legendPosition: 'middle',
          }}
          axisLeft={{
            orient: 'left',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Total # of Unique Users',
            legendOffset: -60,
            legendPosition: 'middle',
          }}
          colors={{ datum: 'color' }} 
          pointSize={8}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          useMesh={true}
          legends={[
            {
              anchor: 'top-left',
              direction: 'row',
              justify: false,
              translateX: 0,
              translateY: -40,
              itemsSpacing: 20,
              itemDirection: 'left-to-right',
              itemWidth: 280,
              itemHeight: 20,
              itemOpacity: 0.75,
              symbolSize: 12,
              symbolShape: 'circle',
              symbolBorderColor: 'rgba(0, 0, 0, .5)',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemBackground: 'rgba(0, 0, 0, .03)',
                    itemOpacity: 1,
                  },
                },
              ],
            },
          ]}
        />
      </div>
    </div>
    </div>
  );
}
